<template>
  <div class="page-container">
    <PageHeader title="反馈管理" />

    <!-- 搜索面板 -->
    <SearchPanel>
      <el-input v-model="query.feedbackNo" clearable placeholder="反馈编号" size="small" style="width: 180px" />
      <el-select v-model="query.feedbackType" clearable placeholder="反馈类型" size="small" @change="handleSearch">
        <el-option label="功能建议" :value="1" />
        <el-option label="问题报错" :value="2" />
        <el-option label="体验优化" :value="3" />
        <el-option label="其他" :value="4" />
      </el-select>
      <el-select v-model="query.sfStatus" clearable placeholder="处理状态" size="small" @change="handleSearch">
        <el-option label="待处理" :value="1" />
        <el-option label="处理中" :value="2" />
        <el-option label="已解决" :value="3" />
        <el-option label="已关闭" :value="4" />
        <el-option label="已驳回" :value="5" />
      </el-select>
      <template #actions>
        <el-button size="small" @click="resetQuery">重置</el-button>
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
      </template>
    </SearchPanel>

    <!-- 反馈列表 -->
    <TablePanel>
      <aj-table
        v-loading="loading"
        :data="list"
        :columns="columns"
        :current-page.sync="query.currentPage"
        :page-size.sync="query.pageSize"
        :page-sizes="[10, 20, 30, 50]"
        :total="total"
        row-id-key="sfId"
        stripe
        use-internal-pagination
        @page-change="handlePageChange"
        @operate-btn-click="handleOperateBtnClick"
      />
      <EmptyState v-if="!loading && list.length === 0" title="暂无反馈记录" description="用户提交反馈后将在此处展示" icon="el-icon-chat-line-round" />
    </TablePanel>

    <!-- 处理反馈弹窗 -->
    <el-dialog title="处理反馈" :visible.sync="handleDialogVisible" width="600px" :close-on-click-modal="false" append-to-body>
      <template v-if="currentRow">
        <!-- 反馈详情 -->
        <div class="fb-handle-info">
          <div class="fb-handle-row">
            <span class="fb-handle-label">反馈编号</span>
            <span class="fb-handle-value">{{ currentRow.feedbackNo || '-' }}</span>
          </div>
          <div class="fb-handle-row">
            <span class="fb-handle-label">提交人</span>
            <span class="fb-handle-value">{{ currentRow.userName || '-' }}</span>
          </div>
          <div class="fb-handle-row">
            <span class="fb-handle-label">反馈类型</span>
            <span class="fb-handle-value">{{ feedbackTypeMap[currentRow.feedbackType] || '其他' }}</span>
          </div>
          <div class="fb-handle-row">
            <span class="fb-handle-label">提交时间</span>
            <span class="fb-handle-value">{{ currentRow.createtime || '-' }}</span>
          </div>
          <div class="fb-handle-row fb-handle-row--block">
            <span class="fb-handle-label">反馈内容</span>
            <span class="fb-handle-value fb-handle-value--pre">{{ currentRow.feedbackContent || '-' }}</span>
          </div>
          <!-- 反馈附件图片 -->
          <template v-if="parseAttachmentUrls(currentRow.feedbackAttachment).length">
            <div class="fb-handle-row fb-handle-row--block">
              <span class="fb-handle-label">反馈图片</span>
              <span class="fb-handle-value">
                <div class="fb-detail-images">
                  <el-image
                    v-for="(url, idx) in parseAttachmentUrls(currentRow.feedbackAttachment)"
                    :key="idx"
                    :src="url"
                    fit="cover"
                    class="fb-detail-thumb"
                    :preview-src-list="parseAttachmentUrls(currentRow.feedbackAttachment)"
                  />
                </div>
              </span>
            </div>
          </template>
        </div>
        <!-- 处理表单 -->
        <el-form ref="handleFormRef" :model="handleForm" :rules="handleFormRules" label-width="80px" style="margin-top: 20px;">
          <el-form-item label="处理状态" prop="sfStatus">
            <el-select v-model="handleForm.sfStatus" placeholder="请选择处理状态" style="width: 100%">
              <el-option label="处理中" :value="2" />
              <el-option label="已解决" :value="3" />
              <el-option label="已关闭" :value="4" />
              <el-option label="已驳回" :value="5" />
            </el-select>
          </el-form-item>
          <el-form-item label="处理回复" prop="handleResult">
            <el-input v-model="handleForm.handleResult" type="textarea" :rows="4" placeholder="请输入处理结果或回复内容…" maxlength="500" show-word-limit />
          </el-form-item>
        </el-form>
      </template>
      <template #footer>
        <el-button size="small" @click="handleDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="submitLoading" @click="submitHandle">确认处理</el-button>
      </template>
    </el-dialog>

    <!-- 查看详情弹窗 -->
    <el-dialog title="反馈详情" :visible.sync="detailDialogVisible" width="600px" append-to-body>
      <template v-if="currentRow">
        <div class="fb-handle-info">
          <div class="fb-handle-row">
            <span class="fb-handle-label">反馈编号</span>
            <span class="fb-handle-value">{{ currentRow.feedbackNo || '-' }}</span>
          </div>
          <div class="fb-handle-row">
            <span class="fb-handle-label">提交人</span>
            <span class="fb-handle-value">{{ currentRow.userName || '-' }}</span>
          </div>
          <div class="fb-handle-row">
            <span class="fb-handle-label">反馈类型</span>
            <span class="fb-handle-value">
              <span class="fb-type-tag" :class="'fb-type--' + currentRow.feedbackType">{{ feedbackTypeMap[currentRow.feedbackType] || '其他' }}</span>
            </span>
          </div>
          <div class="fb-handle-row">
            <span class="fb-handle-label">处理状态</span>
            <span class="fb-handle-value">
              <span class="fb-status-tag" :class="'fb-status--' + currentRow.sfStatus">{{ statusMap[currentRow.sfStatus] || '未知' }}</span>
            </span>
          </div>
          <div class="fb-handle-row">
            <span class="fb-handle-label">提交时间</span>
            <span class="fb-handle-value">{{ currentRow.createtime || '-' }}</span>
          </div>
          <div class="fb-handle-row fb-handle-row--block">
            <span class="fb-handle-label">反馈内容</span>
            <span class="fb-handle-value fb-handle-value--pre">{{ currentRow.feedbackContent || '-' }}</span>
          </div>
          <!-- 反馈附件图片 -->
          <template v-if="parseAttachmentUrls(currentRow.feedbackAttachment).length">
            <div class="fb-handle-row fb-handle-row--block">
              <span class="fb-handle-label">反馈图片</span>
              <span class="fb-handle-value">
                <div class="fb-detail-images">
                  <el-image
                    v-for="(url, idx) in parseAttachmentUrls(currentRow.feedbackAttachment)"
                    :key="idx"
                    :src="url"
                    fit="cover"
                    class="fb-detail-thumb"
                    :preview-src-list="parseAttachmentUrls(currentRow.feedbackAttachment)"
                  />
                </div>
              </span>
            </div>
          </template>
          <!-- 管理员回复 -->
          <template v-if="currentRow.handleResult">
            <div class="fb-handle-row fb-handle-row--block" style="margin-top: 12px;">
              <span class="fb-handle-label">处理回复</span>
              <span class="fb-handle-value fb-handle-value--pre">{{ currentRow.handleResult }}</span>
            </div>
            <div class="fb-handle-row">
              <span class="fb-handle-label">处理人</span>
              <span class="fb-handle-value">{{ currentRow.handlerName || '-' }}</span>
            </div>
            <div class="fb-handle-row">
              <span class="fb-handle-label">处理时间</span>
              <span class="fb-handle-value">{{ currentRow.handletime || '-' }}</span>
            </div>
          </template>
        </div>
      </template>
      <template #footer>
        <el-button size="small" @click="detailDialogVisible = false">关 闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
// 反馈管理页面（管理员）：查看所有用户反馈、处理回复、删除
import { getSysFeedbackList, updateSysFeedbackHandler, deleteSysFeedback } from '@/api/api'
import AjTable from '@/components/ajTable/ajTable.vue'
import EmptyState from '@/components/EmptyState.vue'
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'

const FEEDBACK_TYPE_MAP = { 1: '功能建议', 2: '问题报错', 3: '体验优化', 4: '其他' }
const STATUS_MAP = { 1: '待处理', 2: '处理中', 3: '已解决', 4: '已关闭', 5: '已驳回' }

export default {
  name: 'FeedbackAdmin',
  components: { AjTable, EmptyState, PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      submitLoading: false,
      list: [],
      total: 0,
      // 查询参数
      query: { feedbackNo: '', feedbackType: '', sfStatus: '', currentPage: 1, pageSize: 10 },
      feedbackTypeMap: FEEDBACK_TYPE_MAP,
      statusMap: STATUS_MAP,
      // 处理弹窗
      handleDialogVisible: false,
      currentRow: null,
      handleForm: { sfStatus: '', handleResult: '' },
      handleFormRules: {
        sfStatus: [{ required: true, message: '请选择处理状态', trigger: 'change' }]
      },
      // 详情弹窗
      detailDialogVisible: false
    }
  },
  computed: {
    // 表格列配置
    columns () {
      return [
        { type: 'index', label: '序号', width: 55 },
        { prop: 'feedbackNo', label: '反馈编号', width: 170, showTooltip: true },
        { prop: 'userName', label: '提交人', width: 90 },
        {
          prop: 'feedbackType',
          label: '反馈类型',
          width: 100,
          render: (h, { row }) => {
            const typeMap = { 1: '功能建议', 2: '问题报错', 3: '体验优化', 4: '其他' }
            const colorMap = { 1: '#2563eb', 2: '#dc2626', 3: '#059669', 4: '#7c3aed' }
            const bgMap = { 1: '#eff6ff', 2: '#fef2f2', 3: '#ecfdf5', 4: '#f5f3ff' }
            const borderMap = { 1: '#bfdbfe', 2: '#fecaca', 3: '#a7f3d0', 4: '#c4b5fd' }
            const t = row.feedbackType || 4
            return h('span', {
              style: {
                display: 'inline-flex', alignItems: 'center', padding: '2px 10px', borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', color: colorMap[t], background: bgMap[t], border: `1px solid ${borderMap[t]}`
              }
            }, typeMap[t] || '其他')
          }
        },
        { prop: 'feedbackContent', label: '反馈内容', minWidth: 220, showTooltip: true },
        {
          prop: 'sfStatus',
          label: '处理状态',
          width: 100,
          render: (h, { row }) => {
            const statusMap = { 1: '待处理', 2: '处理中', 3: '已解决', 4: '已关闭', 5: '已驳回' }
            const colorMap = { 1: '#d97706', 2: '#2563eb', 3: '#059669', 4: '#64748b', 5: '#e11d48' }
            const bgMap = { 1: '#fff7ed', 2: '#eff6ff', 3: '#ecfdf5', 4: '#f1f5f9', 5: '#fff1f2' }
            const s = row.sfStatus || 1
            return h('span', {
              style: {
                display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', color: colorMap[s], background: bgMap[s]
              }
            }, statusMap[s] || '未知')
          }
        },
        { prop: 'createtime', label: '提交时间', width: 150 },
        { prop: 'handlerName', label: '处理人', width: 90 },
        { prop: 'handletime', label: '处理时间', width: 150 },
        {
          type: 'operation',
          prop: 'operation',
          label: '操作',
          width: 200,
          fixed: 'right',
          buttons: (row) => {
            const btns = [{ key: 'view', text: '查看', type: 'primary' }]
            // 待处理/处理中状态才显示"处理"按钮
            if (row.sfStatus === 1 || row.sfStatus === 2) {
              btns.push({ key: 'handle', text: '处理', type: 'success', class: 'fb-handle-btn' })
            }
            btns.push({ key: 'delete', text: '删除', type: 'danger' })
            return btns
          }
        }
      ]
    }
  },
  created () {
    this.fetchList()
  },
  methods: {
    // 查询反馈列表
    async fetchList () {
      this.loading = true
      try {
        const params = {}
        if (this.query.feedbackNo) params.feedbackNo = this.query.feedbackNo
        if (this.query.feedbackType) params.feedbackType = this.query.feedbackType
        if (this.query.sfStatus) params.sfStatus = this.query.sfStatus
        params.currentPage = this.query.currentPage
        params.pageSize = this.query.pageSize
        const res = await getSysFeedbackList(params)
        const data = res.data
        if (Array.isArray(data)) {
          this.list = data
          this.total = res.total || data.length
        } else if (data && data.records) {
          this.list = data.records
          this.total = data.total || 0
        } else {
          this.list = []
          this.total = 0
        }
      } catch (e) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    handleSearch () {
      this.query.currentPage = 1
      this.fetchList()
    },
    resetQuery () {
      this.query = { feedbackNo: '', feedbackType: '', sfStatus: '', currentPage: 1, pageSize: 10 }
      this.fetchList()
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.query.currentPage = currentPage
      this.query.pageSize = pageSize
      this.fetchList()
    },

    // 操作按钮分发
    handleOperateBtnClick ({ btn, row }) {
      switch (btn.key) {
        case 'view': this.openDetail(row); break
        case 'handle': this.openHandle(row); break
        case 'delete': this.handleDelete(row); break
      }
    },

    // 查看详情
    openDetail (row) {
      this.currentRow = { ...row }
      this.detailDialogVisible = true
    },

    // 打开处理弹窗
    openHandle (row) {
      this.currentRow = { ...row }
      this.handleForm = { sfStatus: '', handleResult: '' }
      this.handleDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.handleFormRef) this.$refs.handleFormRef.clearValidate()
      })
    },

    // 提交处理结果
    async submitHandle () {
      try {
        await this.$refs.handleFormRef.validate()
      } catch { return }
      this.submitLoading = true
      try {
        await updateSysFeedbackHandler({
          sfId: this.currentRow.sfId,
          sfStatus: this.handleForm.sfStatus,
          handleResult: this.handleForm.handleResult || ''
        })
        this.$message.success('反馈处理成功')
        this.handleDialogVisible = false
        this.fetchList()
      } catch (e) {
        this.$message.error(e.message || '处理失败，请稍后重试')
      } finally {
        this.submitLoading = false
      }
    },

    // 解析附件 JSON 为 URL 数组
    parseAttachmentUrls (attachmentStr) {
      if (!attachmentStr) return []
      try {
        const parsed = JSON.parse(attachmentStr)
        return Array.isArray(parsed) ? parsed.filter(Boolean) : []
      } catch (e) {
        return []
      }
    },

    // 删除反馈
    async handleDelete (row) {
      try {
        await this.$confirm('确认删除该反馈记录？删除后不可恢复。', '提示', { type: 'warning' })
        await deleteSysFeedback({ sfId: row.sfId })
        this.$message.success('反馈记录已删除')
        this.fetchList()
      } catch (e) {
        // 用户取消确认时不提示
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// 反馈类型标签
.fb-type-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
}
.fb-type--1 { color: #2563eb; background: #eff6ff; border: 1px solid #bfdbfe; }
.fb-type--2 { color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; }
.fb-type--3 { color: #059669; background: #ecfdf5; border: 1px solid #a7f3d0; }
.fb-type--4 { color: #7c3aed; background: #f5f3ff; border: 1px solid #c4b5fd; }

// 处理状态标签
.fb-status-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
}
.fb-status--1 { color: #d97706; background: #fff7ed; }
.fb-status--2 { color: #2563eb; background: #eff6ff; }
.fb-status--3 { color: #059669; background: #ecfdf5; }
.fb-status--4 { color: #64748b; background: #f1f5f9; }
.fb-status--5 { color: #e11d48; background: #fff1f2; }

// 内容列截断
.fb-content-cell {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
}

// 弹窗内反馈详情信息
.fb-handle-info {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
}
.fb-handle-row {
  display: flex;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
}
.fb-handle-row--block {
  flex-direction: column;
}
.fb-handle-label {
  width: 80px;
  flex-shrink: 0;
  padding: 10px 14px;
  font-size: 13px;
  color: #64748b;
  background: #f8fafc;
}
.fb-handle-value {
  flex: 1;
  padding: 10px 14px;
  font-size: 14px;
  color: #1e293b;
  word-break: break-all;
}
.fb-handle-value--pre {
  white-space: pre-wrap;
  line-height: 1.7;
}

// 处理按钮醒目样式
::v-deep .aj-table-operation-link.fb-handle-btn.el-link {
  color: #fff !important;
  background: linear-gradient(135deg, #10b981, #059669) !important;
  border-color: transparent !important;
  box-shadow: 0 3px 10px rgba(16, 185, 129, 0.4) !important;
  font-weight: 700 !important;
}
::v-deep .aj-table-operation-link.fb-handle-btn.el-link:hover {
  color: #fff !important;
  background: linear-gradient(135deg, #34d399, #10b981) !important;
  box-shadow: 0 5px 16px rgba(16, 185, 129, 0.55) !important;
  transform: translateY(-1px) !important;
}

// 详情弹窗中图片缩略图
.fb-detail-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.fb-detail-thumb {
  width: 80px;
  height: 80px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover {
    transform: scale(1.06);
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.12);
  }
}

// 操作列按钮间距
::v-deep .aj-table-operation-link {
  margin-right: 10px;
}
::v-deep .aj-table-operation-link:last-child {
  margin-right: 0;
}
</style>
